import { useState, useCallback } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { GET_USER, GET_USER_BY_EMAIL } from '../../../services/graphql/queries';
import { UPDATE_USER, UPDATE_LAST_SEEN } from '../../../services/graphql/mutations';
import { isConfigured, appSyncConfig } from '../../../services/config/appSync';
import {
  AppSyncUser,
  UserInput,
  UserProfileInput,
  CreateUserInput,
  UpdateUserInput,
  GetUserResponse,
  UpdateUserResponse,
  AppSyncError
} from '../types/user';



export function useUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AppSyncError | null>(null);
  const [user, setUser] = useState<AppSyncUser | null>(null);

  // Clear error when starting new operations
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get current user (obtained from auth token in header)
  const getUser = useCallback(async (): Promise<AppSyncUser | null> => {
    setLoading(true);
    clearError();
    
    try {
      console.log('🔍 Fetching current user from AppSync...');
      
      // Get access token from session (AppSync requires access token, not ID token)
      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) {
        throw new Error('No access token available. Please log in.');
      }

      const accessToken = session.tokens.accessToken.toString();
      
      // Use fetch API with access token (AppSync requires access token)
      const endpoint = appSyncConfig.aws_appsync_graphqlEndpoint;
      if (!endpoint) {
        throw new Error('AppSync endpoint not configured');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          query: GET_USER
        })
      });

      if (!response.ok) {
        throw new Error(`AppSync request failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('❌ AppSync GraphQL errors:', result.errors);
        throw new Error(result.errors[0]?.message || 'GraphQL request failed');
      }

      const rawData = result.data.getUser;
      
      // Handle case where user doesn't exist in AppSync yet
      if (!rawData) {
        console.log('⚠️ User not found in AppSync (user may not be created yet)');
        return null;
      }
      
      // GraphQL response already uses camelCase (matches AppSyncUser interface)
      const userData: AppSyncUser = {
        id: rawData.id,
        email: rawData.email || '',
        firstName: rawData.firstName,
        lastName: rawData.lastName,
        nickname: rawData.nickname,
        picture_url: rawData.pictureUrl, // Map pictureUrl from GraphQL to picture_url in AppSyncUser
        birthDate: rawData.birthDate,
        createdAt: rawData.createdAt || new Date().toISOString(),
        lastSeenAt: rawData.lastSeenAt,
      };
      
      setUser(userData);
      return userData;
    } catch (err: any) {
      console.error('❌ Failed to fetch user:', err);
      const error: AppSyncError = {
        message: err.message || 'Failed to fetch user',
        errorType: err.errorType,
        errorInfo: err.errorInfo
      };
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearError, user]);

  // Update user profile using updateUser mutation
  const updateUser = useCallback(async (userInput: UserInput | UserProfileInput): Promise<AppSyncUser | null> => {
    setLoading(true);
    clearError();
    
    try {
      // Get access token from session (AppSync requires access token, not ID token)
      const session = await fetchAuthSession();
      
      if (!session.tokens?.accessToken) {
        throw new Error('No access token available. Please log in.');
      }

      const accessToken = session.tokens.accessToken.toString();
      
      // GraphQL schema uses UserInput with camelCase fields
      const profileInput: UserProfileInput = {
        firstName: (userInput as any).firstName || (userInput as UserProfileInput).firstName,
        lastName: (userInput as any).lastName || (userInput as UserProfileInput).lastName,
        nickname: userInput.nickname,
        picture: userInput.picture,
        birthDate: (userInput as any).birthDate || (userInput as UserProfileInput).birthDate,
        address: userInput.address,
        phoneNumber: (userInput as any).phoneNumber || (userInput as UserProfileInput).phoneNumber,
        locale: userInput.locale,
        zone: userInput.zone,
      };
      
      // Remove undefined fields
      Object.keys(profileInput).forEach(key => {
        if (profileInput[key as keyof UserProfileInput] === undefined) {
          delete profileInput[key as keyof UserProfileInput];
        }
      });
      
      // Use fetch API with access token (AppSync requires access token)
      const endpoint = appSyncConfig.aws_appsync_graphqlEndpoint;
      if (!endpoint) {
        throw new Error('AppSync endpoint not configured');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          query: UPDATE_USER,
          variables: { profile: profileInput }
        })
      });

      if (!response.ok) {
        throw new Error(`AppSync request failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('❌ AppSync GraphQL errors:', result.errors);
        throw new Error(result.errors[0]?.message || 'GraphQL request failed');
      }

      const graphqlResponse = { data: { updateUser: result.data.updateUser } } as { data: UpdateUserResponse };
      
      // GraphQL response already uses camelCase (matches AppSyncUser interface)
      const rawData = graphqlResponse.data.updateUser;
      const userData: AppSyncUser = {
        id: rawData.id,
        email: rawData.email || '',
        firstName: rawData.firstName,
        lastName: rawData.lastName,
        nickname: rawData.nickname,
        birthDate: rawData.birthDate,
        picture_url: rawData.pictureUrl, // Map pictureUrl from GraphQL to picture_url in AppSyncUser
        createdAt: rawData.createdAt || new Date().toISOString(),
        lastSeenAt: rawData.lastSeenAt,
      };
      
      setUser(userData);
      return userData;
    } catch (err: any) {
      console.error('❌ Failed to update user:', err);
      console.error('❌ GraphQL errors:', err.errors);
      const error: AppSyncError = {
        message: err.message || 'Failed to update user',
        errorType: err.errorType,
        errorInfo: err.errorInfo
      };
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Create user (using updateUser since your schema doesn't have createUser)
  const createUser = useCallback(async (input: CreateUserInput): Promise<AppSyncUser | null> => {
    console.log('🆕 Creating user via updateUser mutation...');
    return updateUser(input);
  }, [updateUser]);

  // Update user profile (simplified version of updateUser)
  const updateUserProfile = useCallback(async (
    email: string, 
    profileData: Partial<UserInput>
  ): Promise<AppSyncUser | null> => {
    return updateUser({ email, ...profileData });
  }, [updateUser]);

  // Update only lastSeenAt timestamp - preserves all existing user data
  const updateLastSeen = useCallback(async (userData: AppSyncUser): Promise<AppSyncUser | null> => {
    setLoading(true);
    clearError();
    
    try {
      // Get access token from session (AppSync requires access token, not ID token)
      const session = await fetchAuthSession();
      
      if (!session.tokens?.accessToken) {
        throw new Error('No access token available. Please log in.');
      }

      const accessToken = session.tokens.accessToken.toString();
      
      // Send profile data, backend will update lastSeenAt
      // GraphQL schema uses camelCase
      const profileInput: UserProfileInput = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        // Only send existing data, don't send picture for lastSeen update
      };
      
      // Remove undefined fields
      Object.keys(profileInput).forEach(key => {
        if (profileInput[key as keyof UserProfileInput] === undefined) {
          delete profileInput[key as keyof UserProfileInput];
        }
      });
      
      // Use fetch API with access token (AppSync requires access token)
      const endpoint = appSyncConfig.aws_appsync_graphqlEndpoint;
      if (!endpoint) {
        throw new Error('AppSync endpoint not configured');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          query: UPDATE_LAST_SEEN,
          variables: { profile: profileInput }
        })
      });

      if (!response.ok) {
        throw new Error(`AppSync request failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('❌ AppSync GraphQL errors:', result.errors);
        throw new Error(result.errors[0]?.message || 'GraphQL request failed');
      }

      const graphqlResponse = { data: { updateUser: result.data.updateUser } } as { data: UpdateUserResponse };

      // GraphQL response already uses camelCase (matches AppSyncUser interface)
      const rawData = graphqlResponse.data.updateUser;
      const updatedUserData: AppSyncUser = {
        id: rawData.id,
        email: rawData.email || userData.email,
        firstName: rawData.firstName || userData.firstName,
        lastName: rawData.lastName || userData.lastName,
        nickname: rawData.nickname || userData.nickname,
        birthDate: rawData.birthDate || userData.birthDate,
        picture_url: rawData.pictureUrl || userData.picture_url, // Map pictureUrl from GraphQL to picture_url in AppSyncUser
        createdAt: rawData.createdAt || userData.createdAt,
        lastSeenAt: rawData.lastSeenAt,
      };
      
      console.log('✅ LastSeenAt updated successfully:', updatedUserData);
      
      setUser(updatedUserData);
      return updatedUserData;
    } catch (err: any) {
      console.error('❌ Failed to update lastSeenAt:', err);
      const error: AppSyncError = {
        message: err.message || 'Failed to update lastSeenAt',
        errorType: err.errorType,
        errorInfo: err.errorInfo
      };
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Get current auth session info
  const getCurrentSession = useCallback(async () => {
    try {
    const session = await fetchAuthSession();
    return session;
    } catch (err) {
      console.error('❌ Failed to get current session:', err);
      return null;
    }
  }, []);

  // Auto-fetch user after successful authentication
  const fetchCurrentUser = useCallback(async (): Promise<AppSyncUser | null> => {
    console.log('🔄 Fetching current user after authentication...');
    return getUser();
  }, [getUser]);

  return {
    // State
    user,
    loading,
    error,
    
    // Actions
    getUser,
    createUser,
    updateUser,
    updateUserProfile,
    updateLastSeen,
    fetchCurrentUser,
    getCurrentSession,
    clearError,
    
    // Helpers
    isAuthenticated: !!user,
  };
}
